<template>
    <div>
        <Button outlined :secondary="!running" :primary="running" @click="handleToggle">
            <Icon fixedWidth :icon="running ? 'volume-xmark' : 'volume'" fixed-width />
        </Button>
    </div>
</template>

<script>
import {defineComponent} from 'vue'
import metronomeSound from '@/assets/sounds/metronome.mp3';
import {getAudioContext, installAudioUnlockListeners, playTone, unlockAudioContext} from '@/utils/audio';

export default defineComponent({
  name: 'Metronome',
  mounted() {
    installAudioUnlockListeners();
    this.loadClassicSound();
  },
  beforeUnmount() {
    this.stopMetronome();
  },
  computed: {
    label() {
      return this.running ? 'Stop Metronome' : 'Start Metronome';
    },
  },
  data() {
    return {
      beat: 0,
      classicSoundBuffer: null,
      classicSoundSource: null,
      interval: null,
      running: false,
    };
  },
  methods: {
    decodeAudioBuffer(context, buffer) {
      return new Promise((resolve, reject) => {
        const decode = context.decodeAudioData(buffer, resolve, reject);

        if (decode?.then) {
          decode.then(resolve).catch(reject);
        }
      });
    },
    async handleToggle() {
      if (this.running) {
        this.stopMetronome();
        return;
      }

      await this.startMetronome();
    },
    async loadClassicSound() {
      if (this.classicSoundBuffer) {
        return this.classicSoundBuffer;
      }

      const context = getAudioContext();

      if (!context) {
        return null;
      }

      const response = await fetch(metronomeSound);
      const buffer = await response.arrayBuffer();
      this.classicSoundBuffer = await this.decodeAudioBuffer(context, buffer);

      return this.classicSoundBuffer;
    },
    playBeat() {
      if (this.sound === 'classic') {
        return;
      }

      const pattern = Number(this.pattern || 1);
      const beatNumber = this.beat + 1;
      const accented = pattern > 1 && beatNumber % pattern === 0;

      playTone({
        attack: 0.005,
        duration: accented ? 0.11 : 0.08,
        frequency: accented ? 1040 : 760,
        type: 'triangle',
        volume: accented ? 0.18 : 0.14,
      });

      this.beat += 1;
    },
    async startClassicMetronome() {
      const context = getAudioContext();

      if (!context) {
        return;
      }

      const soundBuffer = await this.loadClassicSound();

      if (!this.running || this.sound !== 'classic' || !soundBuffer) {
        return;
      }

      if (context.state === 'suspended') {
        await context.resume().catch(() => {});
      }

      this.classicSoundSource = context.createBufferSource();
      this.classicSoundSource.buffer = soundBuffer;
      this.classicSoundSource.loop = true;
      this.classicSoundSource.connect(context.destination);
      this.classicSoundSource.start(0);
    },
    async startMetronome() {
      unlockAudioContext();
      this.beat = 0;
      this.running = true;

      if (this.sound === 'classic') {
        await this.startClassicMetronome();
        return;
      }

      this.playBeat();
      this.interval = setInterval(this.playBeat, 1000);
    },
    stopMetronome() {
      this.running = false;

      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }

      if (this.classicSoundSource) {
        this.classicSoundSource.stop();
        this.classicSoundSource.disconnect();
        this.classicSoundSource = null;
      }
    },
  },
  props: {
    pattern: {
      default: 1,
      required: false,
      type: Number,
    },
    sound: {
      default: 'classic',
      required: false,
      type: String,
    },
  },
  watch: {
    sound() {
      if (!this.running) {
        return;
      }

      this.stopMetronome();
      this.startMetronome();
    },
  },
});
</script>

<style scoped lang="scss">

</style>
